/**
 * The world — terrain, water, trees, and the border you cannot cross.
 *
 * The map can be rebuilt into any of Kate's four biomes at runtime. The group vote
 * that decides when that happens is M6; `setBiome` is the mechanism it will call.
 */
import * as THREE from 'three'
import { BIOMES, type BiomeId } from './biomes'
import { MAP_HALF_EXTENT, setWater, WATER } from './map'
import { createGroundTexture, createWaterNormal } from './textures'
import { createForest, type Forest } from './trees'
import { Sky } from './sky'
import { createTerrain, waterSurfaceHeight } from './terrain'
import { createUndergrowth } from './undergrowth'

export interface World {
  scene: THREE.Scene
  sky3d: Sky
  /** A second light for the night, so the world is lit from the moon's side. */
  moon: THREE.DirectionalLight
  forest: Forest
  sun: THREE.DirectionalLight
  sky: THREE.HemisphereLight
  fog: THREE.Fog
  /** Clear-weather fog distances for the current biome; weather scales these down. */
  baseFog: { near: number; far: number }
  /** Exposed so the night can turn up the reflection and put the moon on the water. */
  waterMaterial: THREE.MeshStandardMaterial
  biome: BiomeId
  setBiome(id: BiomeId): void
}

export function createWorld(startBiome: BiomeId = 'jungle'): World {
  const scene = new THREE.Scene()
  const fog = new THREE.Fog(0x8fb7d4, 30, 110)
  scene.fog = fog
  scene.background = new THREE.Color(0x8fb7d4)

  const sun = new THREE.DirectionalLight(0xfff2e0, 2.4)
  sun.castShadow = true
  sun.shadow.mapSize.set(2048, 2048)
  sun.shadow.camera.near = 1
  sun.shadow.camera.far = 200
  sun.shadow.camera.left = -MAP_HALF_EXTENT
  sun.shadow.camera.right = MAP_HALF_EXTENT
  sun.shadow.camera.top = MAP_HALF_EXTENT
  sun.shadow.camera.bottom = -MAP_HALF_EXTENT
  // The shadow map covers the whole 240m map from one 2048 texture, so a texel is about
  // 12cm across. With no bias, gently sloped ground shadows itself in long diagonal
  // bands --- the creases that appeared across every clearing once the world was bright
  // enough to see them. normalBias offsets the lookup along the surface normal, which
  // is the fix that does not also detach shadows from their casters.
  sun.shadow.normalBias = 0.08
  sun.shadow.bias = -0.0005
  scene.add(sun)

  // Moonlight: cool, soft, and its own caster, so objects still have a lit side and a
  // shadowed side at night instead of flattening into silhouettes.
  const moon = new THREE.DirectionalLight(0x9fb6e8, 0)
  moon.castShadow = true
  moon.shadow.mapSize.set(1024, 1024)
  moon.shadow.normalBias = 0.08
  moon.shadow.bias = -0.0005
  moon.shadow.camera.near = 1
  moon.shadow.camera.far = 260
  moon.shadow.camera.left = -MAP_HALF_EXTENT * 0.6
  moon.shadow.camera.right = MAP_HALF_EXTENT * 0.6
  moon.shadow.camera.top = MAP_HALF_EXTENT * 0.6
  moon.shadow.camera.bottom = -MAP_HALF_EXTENT * 0.6
  scene.add(moon)

  const sky = new THREE.HemisphereLight(0xbcd9ff, 0x3d5230, 0.9)
  scene.add(sky)

  // A gradient dome with a real sun, rather than a flat background colour.
  const sky3d = new Sky()
  scene.add(sky3d.mesh)

  const span = MAP_HALF_EXTENT * 2

  const groundMaterial = new THREE.MeshStandardMaterial({
    roughness: 1,
    normalScale: new THREE.Vector2(1.1, 1.1),
  })
  // Real terrain, built from the same height function everything else reads.
  const ground = createTerrain(groundMaterial)
  scene.add(ground)

  const waterNormal = createWaterNormal()
  /*
   * Kate, 2026-08-24: "there's a big white circle in the middle and I don't know where
   * it is... that needs to be fixed. Like a bug or a glitch."
   *
   * It was the pond. At roughness 0.09 with metalness 0.55 this was a chrome disc
   * pointing straight up at a bright sky, so it reflected the sky almost perfectly and
   * came out flat white with a hard circular edge --- a mirror lying on the grass
   * rather than water. Water is a dielectric: its metalness is zero, and what makes it
   * read as water is seeing *into* it, not seeing the sky bounce off it.
   */
  const waterMaterial = new THREE.MeshStandardMaterial({
    color: 0x22566f,
    roughness: 0.22,
    metalness: 0,
    normalMap: waterNormal,
    normalScale: new THREE.Vector2(0.6, 0.6),
    transparent: true,
    opacity: 0.72,
    // Still reflects the sky dome, so the moon sits on the water at night --- just no
    // longer brightly enough to erase the water underneath it.
    envMapIntensity: 0.75,
  })
  // Unit-radius disc, scaled per biome, so the geometry is built once.
  const water = new THREE.Mesh(new THREE.CircleGeometry(1, 48), waterMaterial)
  water.rotation.x = -Math.PI / 2
  water.receiveShadow = true
  scene.add(water)

  scene.add(createBorderWall(span))

  const groundCache = new Map<BiomeId, ReturnType<typeof createGroundTexture>>()
  let undergrowth: THREE.InstancedMesh | null = null

  const world: World = {
    scene,
    sky3d,
    moon,
    // Replaced immediately by setBiome below.
    forest: { group: new THREE.Group(), grid: createForest(0, blankStyle()).grid },
    sun,
    sky,
    fog,
    baseFog: { near: fog.near, far: fog.far },
    waterMaterial,
    biome: startBiome,
    setBiome(id: BiomeId) {
      const spec = BIOMES[id]
      world.biome = id

      let surface = groundCache.get(id)
      if (!surface) {
        surface = createGroundTexture(spec.ground, spec.ground & 0xff)
        groundCache.set(id, surface)
      }
      groundMaterial.map = surface.map
      groundMaterial.normalMap = surface.normalMap
      groundMaterial.needsUpdate = true
      fog.near = spec.fog[0]
      fog.far = spec.fog[1]
      world.baseFog.near = spec.fog[0]
      world.baseFog.far = spec.fog[1]

      setWater(spec.water)
      if (spec.water === 'flooded') {
        // Ocean: the whole map is water, so the disc covers everything.
        water.scale.setScalar(span)
        water.position.set(0, 0.05, 0)
      } else {
        water.scale.setScalar(WATER.radius)
        // Sit the pond on the land rather than at zero, now the ground has height.
        water.position.set(WATER.x, waterSurfaceHeight(), WATER.z)
      }

      if (undergrowth) {
        scene.remove(undergrowth)
        undergrowth.geometry.dispose()
        ;(undergrowth.material as THREE.Material).dispose()
      }
      undergrowth = createUndergrowth({
        color: spec.undergrowth.color,
        count: spec.undergrowth.count,
        height: spec.undergrowth.height,
        waterRadius: spec.water === 'flooded' ? 0 : spec.water,
      })
      scene.add(undergrowth)

      scene.remove(world.forest.group)
      disposeForest(world.forest)
      world.forest = createForest(spec.treeCount, {
        trunk: spec.trunk,
        canopy: spec.canopy,
        canopySpread: spec.canopySpread,
        treeHeight: spec.treeHeight,
        waterRadius: spec.water === 'flooded' ? 0 : spec.water,
      })
      scene.add(world.forest.group)
    },
  }

  world.setBiome(startBiome)
  return world
}

function blankStyle() {
  return { trunk: 0x54402c, canopy: 0x2f5d24, canopySpread: 1, waterRadius: 0,
           treeHeight: [7, 7] as [number, number] }
}

/** Release GPU buffers for the outgoing biome's trees so switching maps doesn't leak. */
function disposeForest(forest: Forest): void {
  forest.group.traverse((node) => {
    if (node instanceof THREE.InstancedMesh) {
      node.geometry.dispose()
      ;(node.material as THREE.Material).dispose()
    }
  })
}

/**
 * A visible edge so the border reads as a wall rather than an invisible stop. The
 * collision itself is in map.ts — this is only how you see where the map ends.
 */
function createBorderWall(span: number): THREE.Group {
  const group = new THREE.Group()
  const height = 8
  const material = new THREE.MeshStandardMaterial({
    color: 0x1d2c17,
    roughness: 1,
    transparent: true,
    opacity: 0.55,
    side: THREE.DoubleSide,
  })
  const sides: Array<[number, number, number]> = [
    [0, -MAP_HALF_EXTENT, 0],
    [0, MAP_HALF_EXTENT, Math.PI],
    [-MAP_HALF_EXTENT, 0, Math.PI / 2],
    [MAP_HALF_EXTENT, 0, -Math.PI / 2],
  ]
  for (const [x, z, rotation] of sides) {
    const wall = new THREE.Mesh(new THREE.PlaneGeometry(span, height), material)
    wall.position.set(x, height / 2, z)
    wall.rotation.y = rotation
    group.add(wall)
  }
  return group
}
